import { useCallback, useMemo, useEffect } from 'react';
import {
  ProjectClips_V2QueryVariables,
  useProjectClips_V2Query,
} from '../../operation.generated';
import { ProjectClipsOrderBy } from '../../../../../server/graphql/__generated.types';
import { ClipNode, Highlights } from './utils/constants';
import { usePersistentClipStateAndActions } from './common';
import { sanitizeSearchTerm } from './useClipsSearch';
import usePrevious from '../../../../lib/usePrevious';

type ClipNodeWithHighlights = ClipNode & { highlights: Highlights };

const DEFAULT_CLIPS_PAGE_SIZE = 32;
const MIN_SEARCH_LIMIT = 3;

const useProjectClipsQuery = (projectId: string, skip: boolean = false) => {
  const { search } = usePersistentClipStateAndActions();

  const sanitizedSearch = useMemo(() => {
    const sanitized = sanitizeSearchTerm(search || '');
    return sanitized.length >= MIN_SEARCH_LIMIT ? search : null;
  }, [search]);

  const variables = useMemo<ProjectClips_V2QueryVariables>(() => {
    return {
      projectInputs: {
        projectId,
      },
      clipsInputs: {
        limit: DEFAULT_CLIPS_PAGE_SIZE,
        orderBy: ProjectClipsOrderBy.SequenceDesc,
        search: sanitizedSearch,
      },
    };
  }, [projectId, sanitizedSearch]);

  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
    networkStatus,
    variables: resultVariables,
  } = useProjectClips_V2Query({
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
  });

  // Refetch on Search
  const previousSanitizedSearch = usePrevious(sanitizedSearch);
  useEffect(() => {
    if (
      // checking for 'undefined for prevent this from running mount.
      previousSanitizedSearch !== undefined &&
      sanitizedSearch !== previousSanitizedSearch
    ) {
      refetch(variables);
    }
  }, [refetch, variables, sanitizedSearch, previousSanitizedSearch]);

  const clipInfo = data?.user.project?.clips_V2;
  const hasNextPage = Boolean(clipInfo?.pageInfo?.hasNextPage);
  const endCursor = clipInfo?.pageInfo?.endCursor;
  const loadMore = useCallback(async () => {
    if (hasNextPage && !(networkStatus < 7)) {
      fetchMore({
        variables: {
          ...variables,
          clipsInputs: {
            ...variables.clipsInputs,
            cursor: endCursor,
          },
        },
      });
    }
  }, [fetchMore, endCursor, hasNextPage, networkStatus, variables]);

  // preventing duplicate nodes from occurring at the query level
  // we could use the general utility here - but for performance
  // reasons - combining filtering and deduping here
  const filteredAndSortedClips = useMemo<ClipNodeWithHighlights[]>(() => {
    const clipInfoEdges = clipInfo?.edges || [];
    const nodes = [];
    const map: Record<string, string> = {};
    for (const edge of clipInfoEdges) {
      if (!map[edge.node.id] && !edge.node.isDeleted) {
        nodes.push({ ...edge.node, highlights: edge.highlights });
      }
      map[edge.node.id] = edge.node.id;
    }
    const sorted = nodes.sort((a, b) => (a.sequence < b.sequence ? 1 : -1));
    return sorted;
  }, [clipInfo?.edges]);

  return useMemo(
    () => ({
      clips: filteredAndSortedClips,
      variables: resultVariables,
      loading,
      error,
      fetchMore: loadMore,
      networkStatus,
      hasNextPage,
    }),
    [
      filteredAndSortedClips,
      resultVariables,
      loading,
      error,
      networkStatus,
      loadMore,
      hasNextPage,
    ]
  );
};

export default useProjectClipsQuery;
